// apps/guide/onboarding/WelcomeScreen.tsx
import React, { CSSProperties } from 'react';
import { Header } from '@ui/components/Header';
import { Footer } from '@ui/components/Footer';
import { getSupabaseClient } from "../lib/supabase";
import { useNavigate } from 'react-router-dom';

const ICON_MASK_STYLE: CSSProperties = {
  backgroundColor: '#D29B04',
  maskImage: 'url(/icon.svg)',
  WebkitMaskImage: 'url(/icon.svg)',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskPosition: 'center',
  maskSize: 'contain',
  WebkitMaskSize: 'contain',
};

const steps = [
  'Connect with seekers who need your expertise',
  'Set your own rates and availability',
  'Work in a safe, verified environment',
  'Grow your practice with powerful tools',
];

interface WelcomeScreenProps {
  onGetStarted: () => void;
}

export default function WelcomeScreen({ onGetStarted }: WelcomeScreenProps) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    const supabase = getSupabaseClient();
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleGetStarted = () => {
    // Mark that user has seen the welcome screen
    localStorage.setItem('onboarding_started', 'true');
    onGetStarted();
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#FFFCEF]">
      <Header variant="onboarding" onLogout={handleLogout} />

      {/* Main content */}
      <main className="flex flex-1 flex-col px-6 lg:px-0">
        <div className="mx-auto w-full max-w-lg lg:max-w-2xl py-10 lg:py-16">
          {/* Welcome heading */}
          <h1 className="font-recoleta text-[32px] lg:text-[48px] font-normal leading-[1.1] text-[#214D3C] mb-6">
            Welcome to{'\n'}Guides Collective
          </h1>

          {/* Intro copy */}
          <p className="text-[16px] lg:text-[18px] leading-[1.6] text-[#214D3C] mb-2">
            Let's get you set up to connect with seekers who need your expertise.
          </p>
          <p className="text-[16px] lg:text-[18px] leading-[1.6] text-[#214D3C] mb-10">
            It only takes a few minutes to complete your profile and start accepting clients.
          </p>

          {/* What to expect card */}
          <div className="rounded-2xl border border-[#214D3C]/20 bg-[#FFFCEF] p-6 lg:p-8 mb-10">
            <h2 className="font-recoleta text-[18px] lg:text-[20px] font-medium text-[#214D3C] mb-6">
              Join Our Community
            </h2>
            <div className="flex flex-col gap-5">
              {steps.map((step) => (
                <div key={step} className="flex items-start gap-3">
                  <span
                    className="inline-block h-5 w-5 flex-shrink-0 mt-1"
                    style={ICON_MASK_STYLE}
                    aria-hidden
                  />
                  <span className="font-recoleta text-[18px] lg:text-[22px] text-[#214D3C]">
                    {step}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA button */}
          <button
            type="button"
            onClick={handleGetStarted}
            className="w-full flex items-center justify-center gap-3 rounded-full bg-[#214D3C] px-6 py-4 text-[18px] font-medium text-[#FFFCEF] transition-colors duration-200 hover:bg-[#1a3d2f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#214D3C]"
          >
            <span>Get Started</span>
            <span className="text-lg">{'\u2192'}</span>
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
